import { useState, type FunctionComponent } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  PostStatus,
  type IPost,
} from "../../../../types/models/post.models.types";
import { Button } from "../../../../components/ui/button";
import { CampaignPostActionAdminPartial } from "./campaign-post-action.admin.partials";
import { CampaignPostMediaAdminPartial } from "./campaign-post-media.admin.partials";
import { Checkbox } from "../../../../components/ui/checkbox";
import { useToggleProductRefundAdminHttp } from "../../../../hooks/http/admin/posts/toggle-product-refund.admin.http";
import { autoReviewStatus, handleHttpError } from "../../../../utils/utils";
import { useToast } from "../../../../hooks/use-toast";
import Spinner from "../../../../components/spinner.components";
import { DateTime } from "luxon";
import useCampaignStore from "../../../../store/campaign.store";
import { useChangePostVisibilityAdminHttp } from "../../../../hooks/http/admin/posts/change-post-visibility.admin.http";
import RightArrowIcon from "../../../../assets/svgs/common/right-arrow.svg";
import RejectionCard from "../../../../components/rejection-card.components";
import { AccountType } from "../../../../constants/account-types.constants";
import PostMetric from "../../../../components/post-metrics.components";
import {
  SocialApplicationStatus,
  SocialPlatforms,
} from "../../../../types/models/social.models.types";
import { Badge } from "../../../../components/ui/badge";

type AppProps = {
  post: IPost;
};

export const CampaignPostAdminPartial: FunctionComponent<AppProps> = ({
  post,
}) => {
  const location = useLocation();
  const { data: campaignData, fetching: fetchingCampaignData } =
    useCampaignStore();
  const [eligibleForProductRefund, setEligibleForProductRefund] = useState(
    post.eligibleForProductRefund,
  );
  const [visibleToBrand, setVisibleToBrand] = useState(post.visibleToBrand);
  const { execute: toggleProductRefundHttp, fetching: fetchingtoggle } =
    useToggleProductRefundAdminHttp();
  const {
    execute: changePostVisibilityHttp,
    fetching: fetchingChangePostVisibility,
  } = useChangePostVisibilityAdminHttp();

  const { toast } = useToast();

  async function handleProductRefund() {
    const response = await toggleProductRefundHttp({
      body: {
        eligibleForProductRefund: (!eligibleForProductRefund).toString(),
      },
      pathParams: {
        id: post.id,
      },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setEligibleForProductRefund(!eligibleForProductRefund);
  }

  async function handlePostVisibility() {
    const response = await changePostVisibilityHttp({
      body: {
        visibleToBrand: (!visibleToBrand).toString(),
      },
      pathParams: {
        id: post.id,
      },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setVisibleToBrand(!visibleToBrand);
  }

  function handlePostStatus() {
    if (post.postStatus.status === PostStatus.postApproved) {
      return "Live post";
    }
    if (post.postStatus.status === PostStatus.flagged) {
      return "Live posts: flagged";
    }
    if (post.postStatus.status === PostStatus.flaggedReview) {
      return "Live posts: flagged review";
    }
    if (post.postStatus.status === PostStatus.disqualify) {
      return "Live posts: disqualified";
    }
    if (post.postStatus.status === PostStatus.post) {
      return "Post submission: pending";
    }
    if (post.postStatus.status === PostStatus.postRejected) {
      return "Post submission: rejected";
    }
    if (post.postStatus.status === PostStatus.draft) {
      return "Draft submission: pending";
    }
    if (post.postStatus.status === PostStatus.draftApproved) {
      return "Draft submission: approved";
    }
    if (post.postStatus.status === PostStatus.draftRejected) {
      return "Draft submission: rejected";
    }
    return "";
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      <div
        className={
          "flex flex-col justify-between items-center   rounded-lg w-full font-semibold "
        }
      >
        {post.hasUserDeletedPost &&
          (post.postStatus.status === PostStatus.postApproved ||
            post.postStatus.status === PostStatus.flagged ||
            post.postStatus.status === PostStatus.flaggedReview ||
            post.postStatus.status === PostStatus.disqualify) && (
            <RejectionCard
              title="Post deleted"
              message="The user might have deleted the post, please verify on instagram before taking action."
              status="postDeleted"
            />
          )}
        {post.autoReviewStatus &&
          post.postStatus.status === PostStatus.post && (
            <RejectionCard
              title={autoReviewStatus(post.autoReviewStatus.status)}
              message={post.autoReviewStatus.message || ""}
              status={post.autoReviewStatus.status}
            />
          )}
        <div
          className={`flex justify-between items-center px-4 py-3 border  border-border  w-full ${
            (post.hasUserDeletedPost &&
              (post.postStatus.status === PostStatus.postApproved ||
                post.postStatus.status === PostStatus.flagged ||
                post.postStatus.status === PostStatus.flaggedReview ||
                post.postStatus.status === PostStatus.disqualify)) ||
            (post.autoReviewStatus &&
              post.postStatus.status === PostStatus.post)
              ? "rounded-b-lg border-t-0"
              : "rounded-lg"
          }`}
        >
          <span className="text-xs">Show more details</span>
          <div className="flex items-center gap-4">
            <Link to={`${location.pathname}/${post.id}`}>
              <RightArrowIcon />
            </Link>
          </div>
        </div>
      </div>
      {[PostStatus.post, PostStatus.postApproved, PostStatus.postRejected, PostStatus.flagged, PostStatus.flaggedReview, PostStatus.disqualify].includes(post.postStatus.status as PostStatus) && (
        <PostMetric post={post} />
      )}
      {post.postStatus.status === "flagged" && (
        <div className="flex items-start gap-1 px-3 py-4 border border-border rounded-lg font-semibold text-secondary">
          Reason:
          <span className="max-h-12 text-white text-wrap truncate">
            {post.postStatus.message}
          </span>
        </div>
      )}
      <div className="flex flex-col border border-border rounded-lg w-full overflow-hidden">
        <div
          className={`relative flex justify-between items-center px-4 ${post.postStatus.status === PostStatus.postApproved ? "pt-8" : "pt-4"}  w-full`}
        >
          {(post.postStatus.status === PostStatus.postApproved ||
            post.postStatus.status === PostStatus.vendorDraft) &&
          post.addedByUser?.accountType === AccountType.ADMIN ? (
            <div className="top-0 left-0 absolute bg-primarybuttonbg px-2 py-1 rounded-br-10 font-semibold text-black text-xs">
              added by {post.addedByUser.email}
            </div>
          ) : (
            ""
          )}
          <div className="flex flex-col font-semibold text-left text-primary">
            <p className="text-base flex items-center ">
              {campaignData.socialPlatform === SocialPlatforms.instagram &&
                post.social?.handle}
              {campaignData.socialPlatform === SocialPlatforms.youtube &&
                post.social?.youtube?.channelHandle}
              {post.social?.applicationStatus?.status ===
                SocialApplicationStatus.rejected && (
                <Badge className="border scale-75 border-rejectbuttonborder bg-rejectbuttonbg text-rejectbuttontext">
                  banned
                </Badge>
              )}
            </p>
            {post.postStatus.status === PostStatus.draft ? (
              <span className="text-secondary text-xs">
                Submitted on{" "}
                {post.postStatus.timestamp
                  ? (() => {
                      const dt = DateTime.fromISO(
                        post.postStatus.timestamp.toString(),
                      );
                      return `${dt.toFormat("h:mm a")} - ${dt.toLocaleString(DateTime.DATE_SHORT)}`;
                    })()
                  : "-"}
              </span>
            ) : (
              <span className="text-secondary text-xs">
                {post.postStatus.timestamp
                  ? (() => {
                      const dt = DateTime.fromISO(
                        post.postStatus.timestamp.toString(),
                      );
                      return `${dt.toFormat("h:mm a")} - ${dt.toLocaleString(DateTime.DATE_SHORT)}`;
                    })()
                  : "-"}
              </span>
            )}
          </div>
          <Link to={`profile/${post.social?.id}${location.search}`}>
            <Button className="bg-tertiarybuttonbg px-4 py-2 border border-border font-semibold">
              View profile
            </Button>
          </Link>
        </div>
        <div className="py-3 p-2">
          <Button
            className={`bg-tertiarybuttonbg px-4 border border-border tracking-wide h-8 font-semibold text-xs ${window.location.pathname.includes("search") ? "flex" : "hidden"}`}
          >
            {handlePostStatus()}
          </Button>
        </div>
        <CampaignPostMediaAdminPartial campaign={campaignData} post={post} />
      </div>
      <div className="flex flex-col gap-4">
        {campaignData.productRefund && (
          <div className="flex items-center gap-2">
            {fetchingtoggle || fetchingCampaignData ? (
              <Spinner width={16} />
            ) : (
              <Checkbox
                className="border border-border"
                checked={eligibleForProductRefund}
                onClick={() => {
                  handleProductRefund();
                }}
              />
            )}

            <span className="font-semibold text-base text-primary cursor-pointer">
              Eligible for product refund
            </span>
          </div>
        )}
        {(post.postStatus.status === PostStatus.draft ||
          post.postStatus.status === PostStatus.draftApproved ||
          post.postStatus.status === PostStatus.draftRejected) &&
          post.social?.applicationStatus?.status !==
            SocialApplicationStatus.rejected && (
          <div className="flex items-center gap-2">
            {fetchingChangePostVisibility || fetchingCampaignData ? (
              <Spinner width={16} />
            ) : (
              <Checkbox
                className="border border-border"
                checked={visibleToBrand}
                onClick={() => {
                  handlePostVisibility();
                }}
              />
            )}

            <span className="font-semibold text-base text-primary cursor-pointer">
              Show to brand
            </span>
          </div>
        )}
      </div>
      {post.social?.applicationStatus?.status !==
        SocialApplicationStatus.rejected && (
        <CampaignPostActionAdminPartial post={post} />
      )}
    </div>
  );
};
